import Navbar from "@/components/Navbar";
import TokenDisplay from "@/components/TokenDisplay";
import GibRating from "@/components/GibRating";
import UserChannels from "@/components/UserChannels";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import ImageHoverPopup from "@/components/ui/image-hover-popup";
import { Badge } from "@/components/ui/badge";
import { Building2, Briefcase, Users, Target, MapPin, Mail, Linkedin, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const HeatherEdwardsProfile = () => {
  const user = {
    name: "Heather Edwards",
    role: "GSA Knowledge Network Ambassador",
    title: "Chief of Staff & Acting Director of Community Planning and Design, PBS Architecture & Engineering",
    email: "heather.edwards@gsa.gov",
    linkedIn: "https://www.linkedin.com/in/heather-edwards/",
    location: "Remote",
    solutionBounties: 5,
    domains: [
      "Workplace Strategy",
      "Project Management",
      "Organizational Leadership",
      "Visual Communication",
      "Federal Operations",
      "Community Planning",
      "Team Development",
      "Process Optimization"
    ],
    industries: [
      { name: "Government", icon: Briefcase },
      { name: "Workplace Strategy", icon: Target },
      { name: "Leadership", icon: Users },
      { name: "Architecture", icon: Building2 }
    ],
    confirmedSolutions: 15,
    confirmedKnowledgeBases: 32,
    tokenBalance: 145
  };

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="p-6 col-span-2">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-start space-x-6">
                <div className="flex flex-col items-center gap-4">
                  <ImageHoverPopup 
                    imageSrc="/lovable-uploads/d993a42a-753a-4ac2-b329-871a797be984.png"
                    imageAlt="Heather Edwards"
                  >
                    <Avatar className="h-36 w-36 cursor-pointer">
                      <AvatarImage src="/lovable-uploads/d993a42a-753a-4ac2-b329-871a797be984.png" alt="Heather Edwards" className="object-cover" />
                      <AvatarFallback className="bg-teal-100 text-teal-800 text-2xl">HE</AvatarFallback>
                    </Avatar>
                  </ImageHoverPopup>
                  <UserChannels />
                  <div className="w-full">
                    <h3 className="font-serif text-xl font-semibold text-black mb-3">Industries</h3>
                    <div className="grid grid-cols-1 gap-2">
                      {user.industries.map((industry) => {
                        const Icon = industry.icon;
                        return (
                          <div 
                            key={industry.name}
                            className="flex items-center space-x-2 p-3 border-2 border-black rounded-lg"
                          >
                            <Icon className="h-5 w-5 text-black" />
                            <span className="text-sm font-medium text-black">{industry.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ImageHoverPopup 
                      imageSrc="/lovable-uploads/d993a42a-753a-4ac2-b329-871a797be984.png"
                      imageAlt="Heather Edwards"
                    >
                      <h2 className="font-serif text-3xl font-bold text-black cursor-pointer hover:text-primary transition-colors">{user.name}</h2>
                    </ImageHoverPopup>
                    <a href={`mailto:${user.email}`} className="inline-flex items-center text-blue-600 hover:text-blue-800">
                      <Mail size={18} className="mr-1" />
                      <span className="text-sm">{user.email}</span>
                    </a>
                  </div>
                  <div className="flex items-center mb-1">
                    <p className="text-lg font-medium text-black">{user.title}</p>
                  </div>
                  <div className="flex items-center mb-4">
                    <p className="text-md text-gray-600">{user.role}</p>
                    <Link to="/gsa-channel" className="flex items-center hover:opacity-90 ml-2">
                      <img
                        src="/lovable-uploads/3e0cc269-0dd5-40b5-a1f0-0ebbe7e85733.png"
                        alt="GSA"
                        className="h-10 w-10 hover:scale-110 transition-transform duration-200"
                      />
                    </Link>
                  </div>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="h-4 w-4 text-gray-600" />
                    <span className="text-gray-600">{user.location}</span>
                    <a 
                      href={user.linkedIn} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center text-blue-600 hover:text-blue-800 ml-2"
                    >
                      <Linkedin size={16} />
                      <ExternalLink size={14} className="ml-1" />
                    </a>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Solution Bounties</h3>
                        <span className="text-4xl font-bold mb-2">{user.solutionBounties}</span>
                        <Link 
                          to="/solution-bounties/heather"
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          View Details →
                        </Link>
                      </div>
                    </Card>
                    <GibRating rating={8.4} />
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Confirmed Solutions</h3>
                        <span className="text-4xl font-bold">{user.confirmedSolutions}</span>
                      </div>
                    </Card>
                    <Card className="p-6">
                      <div className="flex flex-col items-center">
                        <h3 className="font-serif text-xl font-semibold text-black mb-2">Knowledge Bases</h3>
                        <span className="text-4xl font-bold">{user.confirmedKnowledgeBases}</span>
                      </div>
                    </Card>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Key Experience</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-black">Chief of Staff & Acting Director of Community Planning and Design</h4>
                  <p className="text-gray-600 text-sm">GSA PBS Architecture & Engineering | Jan 2025 - Present</p>
                  <p className="text-gray-700 mt-2">
                    Serving as trusted advisor to executive leadership while balancing dual responsibilities in strategic operations 
                    and community planning. Managing multi-million-dollar financial operations and leading teams of community planners, 
                    GIS experts, and landscape architects.
                  </p>
                </div>
                <div className="border-l-4 border-teal-500 pl-4">
                  <h4 className="font-semibold text-black">Senior Workplace Strategist</h4>
                  <p className="text-gray-600 text-sm">GSA | Oct 2012 - Present | 12+ years</p>
                  <p className="text-gray-700 mt-2">
                    Orchestrated federal workplace initiatives, successfully delivering award-winning projects exceeding $10M+. 
                    Led the "Wild Ideas Cadre," assembling inter-agency experts to prototype transformative workplace strategies 
                    in response to post-pandemic needs.
                  </p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-black">Regional Workplace Executive</h4>
                  <p className="text-gray-600 text-sm">GSA | Sep 2009 - Present | 15+ years</p>
                  <p className="text-gray-700 mt-2">
                    Served as regional advisor in federal workplace strategy, collaborating with agencies to modernize and optimize 
                    space planning. Secured funding and customer commitments for regional workplace engagements through innovative 
                    contracting approaches.
                  </p>
                </div>
              </div>
            </div>
          </Card>
          
          <div className="space-y-6">
            <TokenDisplay balance={user.tokenBalance} />
            <Card className="p-6">
              <h3 className="font-serif text-xl font-semibold text-black mb-3">Domains</h3>
              <div className="flex flex-wrap gap-2">
                {user.domains.map((domain) => (
                  <Badge 
                    key={domain} 
                    variant="outline"
                    className="border-2 border-black text-black"
                  >
                    {domain}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HeatherEdwardsProfile;